import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  FormLabel,
  Flex,
  Textarea,
  useTheme,
  Text,
  FormErrorMessage,
} from "@chakra-ui/react";
import SubmitButton from "../../buttons/SubmitButton";
import { Select } from "chakra-react-select";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import { getAllSubjects } from "../../../store/slice/subjectSlice";
import { getAllChapters } from "../../../store/slice/chapterSlice";
import { createTopic, updateTopic } from "../../../store/slice/topicSlice";
import { useDispatch, useSelector } from "react-redux";
const AddTopicModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();
  const {
    placeholderOuterColor,
    placeholderTextColor,
    deleteBtnColor,
    primaryBlueLight,
    primaryBlue,
  } = theme.colors.pallete;
  const { subjects } = useSelector((state) => state.subject);
  const { chapters } = useSelector((state) => state.chapters);

  const [formDataError, setFormDataError] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({
    subjectId: "",
    chapterId: "",
    name: "",
    description: "",
    displayOrder: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          subjectId: editData.subjectId || "",
          chapterId: editData.chapterId,
          name: editData.name,
          description: editData.description,
          displayOrder: editData.displayOrder,
        });
      } else {
        setFormData({
          subjectId: "",
          chapterId: "",
          name: "",
          description: "",
          displayOrder: "",
        });
      }
    }
  }, [isOpen, editData]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subjectId: "",
        chapterId: "",
        name: "",
        description: "",
        displayOrder: "",
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllSubjects());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllChapters());
    }
  }, [isOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    setFormDataError((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subjectId: "",
        chapterId: "",
        name: "",
        description: "",
        displayOrder: "",
      });
      setFormDataError({});
    }
  }, [isOpen]);

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      subjectId: Number(selectedOption.value),
      chapterId: "",
    }));
    setFormDataError((prev) => ({ ...prev, subjectId: "" }));
  };

  const handleChapterChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      chapterId: Number(selectedOption.value),
    }));
    setFormDataError((prev) => ({ ...prev, chapterId: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.subjectId) newErrors.subjectId = "Subject is required.";
    if (!formData.chapterId) newErrors.chapterId = "Chapter is required.";
    if (!formData.name) newErrors.name = "Topic is required.";
    if (!formData.description)
      newErrors.description = "Description is required.";
    const displayOrder = formData.displayOrder?.toString().trim();
    if (!displayOrder) {
      newErrors.displayOrder = "Display Order is required.";
    } else if (isNaN(displayOrder) || Number(displayOrder) <= 0) {
      newErrors.displayOrder = "Display Order must be a number.";
    }
    setFormDataError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      subjectId: formData.subjectId,
      chapterId: formData.chapterId,
      name: formData.name,
      description: formData.description,
      displayOrder: Number(formData.displayOrder),
    };
    setIsSubmitLoading(true);

    if (editData) {
      dispatch(updateTopic({ id: editData.id, formData }))
        .unwrap()
        .then((response) => {
          addNotification("Topic updated successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 3000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    } else {
      dispatch(createTopic(payload))
        .unwrap()
        .then((response) => {
          addNotification("Topic created successfully", "success", 3000);

          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 3000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={"18px"} fontWeight={400} textAlign="center">
            {editData ? "Update Topic" : " Add Topic"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isRequired isInvalid={!!formDataError.subjectId}>
              <FormLabel>Subject</FormLabel>
              <Select
                fontSize="14px"
                borderColor={placeholderOuterColor}
                placeholder="Select Subject"
                value={
                  formData.subjectId
                    ? {
                        value: formData.subjectId.toString(),
                        label:
                          subjects.find((s) => s.id === formData.subjectId)
                            .name ?? "",
                      }
                    : null
                }
                onChange={handleSelectChange}
                options={subjects.map((subject) => ({
                  value: subject.id.toString(),
                  label: subject.name,
                }))}
                isReadOnly={!!editData}
                useBasicStyles
              ></Select>
              <FormErrorMessage>{formDataError.subjectId}</FormErrorMessage>
              {editData && (
                <Text fontSize="12px" color="gray.500" mt={1}>
                  Subject name cannot be edited after creation.
                </Text>
              )}
            </FormControl>

            <FormControl
              mt={4}
              isRequired
              isInvalid={!!formDataError.chapterId}
            >
              <FormLabel>Chapter</FormLabel>
              <Select
                fontSize="14px"
                borderColor={placeholderOuterColor}
                placeholder="Select chapter"
                value={
                  formData.chapterId
                    ? {
                        value: formData.chapterId.toString(),
                        label:
                          chapters.find((c) => c.id === formData.chapterId)
                            .name ?? "",
                      }
                    : null
                }
                // onChange={(selectedOption) =>
                //   setFormData((prev) => ({
                //     ...prev,
                //     chapterId: Number(selectedOption.value),
                //   }))
                // }
                onChange={handleChapterChange}
                options={chapters
                  .filter(
                    (chapter) =>
                      chapter.subjectId === Number(formData.subjectId)
                  )
                  .map((chapter) => ({
                    value: chapter.id.toString(),
                    label: chapter.name,
                  }))}
                isReadOnly={!!editData}
                useBasicStyles
                isDisabled={!formData.subjectId}
              />
              <FormErrorMessage>{formDataError.chapterId}</FormErrorMessage>
              {editData && (
                <Text fontSize="12px" color="gray.500" mt={1}>
                  Chapter name cannot be edited after creation.
                </Text>
              )}
            </FormControl>

            <FormControl isRequired mt={4} isInvalid={!!formDataError.name}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                fontSize={"14px"}
                borderColor={placeholderOuterColor}
                _placeholder={{ color: placeholderTextColor }}
                placeholder="Enter topic name"
                value={formData.name}
                onChange={handleChange}
              />
              <FormErrorMessage>{formDataError.name}</FormErrorMessage>
            </FormControl>

            <FormControl
              mt={4}
              isRequired
              isInvalid={!!formDataError.description}
            >
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                borderColor={placeholderOuterColor}
                _placeholder={{ color: placeholderTextColor }}
                fontSize={"14px"}
                placeholder="Enter Description"
                value={formData.description}
                onChange={handleChange}
              />
              <FormErrorMessage>{formDataError.description}</FormErrorMessage>
            </FormControl>

            <FormControl
              mt={4}
              isRequired
              isInvalid={!!formDataError.displayOrder}
            >
              <FormLabel>Display Order</FormLabel>
              <Input
                name="displayOrder"
                fontSize={"14px"}
                borderColor={placeholderOuterColor}
                _placeholder={{ color: placeholderTextColor }}
                placeholder="Enter display order"
                value={formData.displayOrder}
                onChange={handleChange}
              />
              <FormErrorMessage>{formDataError.displayOrder}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Flex w={"full"} justifyContent={"center"}>
              <SubmitButton
                text={editData ? "Update" : "Submit"}
                backColor={primaryBlue}
                textColor="white"
                hoverColor={primaryBlueLight}
                isLoading={isSubmitLoading}
                onClickHandler={handleSubmit}
              />
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddTopicModal;
